import { useEffect, useState } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import {
  fetchRuntimeConfig,
  createMsalConfig,
  isDevelopmentAuth
} from '../config/runtimeConfig';
import { AuthStateProvider } from '../contexts/AuthContext';

// Create MSAL instance only if not in development auth mode
let msalInstance = null;
let runtimeConfig = null;

const initializeMsalWithRuntimeConfig = async () => {
  try {
    runtimeConfig = await fetchRuntimeConfig();

    if (isDevelopmentAuth(runtimeConfig)) {
      console.log('🔧 Development auth enabled - skipping MSAL initialization');
      return true;
    }

    if (!runtimeConfig.auth.azure.clientId) {
      console.error('❌ Azure AD Client ID not configured');
      return false;
    }

    const msalConfig = createMsalConfig(runtimeConfig);
    msalInstance = new PublicClientApplication(msalConfig);

    await msalInstance.initialize();

    // IMPORTANT: Handle redirect promise for redirect flow
    // This must be called on every page load when using redirect flow
    try {
      const response = await msalInstance.handleRedirectPromise();
      if (response && response.account) {
        msalInstance.setActiveAccount(response.account);
        console.log('✅ Handled redirect response, user authenticated');
      }
    } catch (redirectError) {
      console.error('Error handling redirect:', redirectError);
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }

    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
        const account = event.payload.account;
        msalInstance.setActiveAccount(account);
      }
    });

    console.log('✅ MSAL initialized with runtime config');
    return true;
  } catch (error) {
    console.error('❌ MSAL initialization failed:', error);
    return false;
  }
};

const AuthProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDevMode, setIsDevMode] = useState(false);

  useEffect(() => {
    initializeMsalWithRuntimeConfig().then((success) => {
      if (success) {
        setIsDevMode(runtimeConfig && isDevelopmentAuth(runtimeConfig));
        setIsInitialized(true);
      } else {
        console.error('Failed to initialize authentication');
      }
    });
  }, []);

  if (!isInitialized) {
    return <div>Loading authentication...</div>;
  }

  // Dev auth mode: no MsalProvider needed, AuthStateProvider stands alone
  if (isDevMode) {
    return (
      <AuthStateProvider>
        {children}
      </AuthStateProvider>
    );
  }

  if (!msalInstance) {
    return <div>Authentication configuration error</div>;
  }

  // Production mode: AuthStateProvider is inside MsalProvider so it can
  // call useMsal() and receive real MSAL state (accounts, inProgress, etc.)
  return (
    <MsalProvider instance={msalInstance}>
      <AuthStateProvider>
        {children}
      </AuthStateProvider>
    </MsalProvider>
  );
};

export default AuthProvider;
